import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StateSelect } from "@/components/StateSelect";

export default function AuthPage({ mode }) {
  const isReg = mode === "register";
  const { onAuth } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [f, setF] = useState({ name: "", email: "", password: "", state: "QLD", suburb: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const next = sp.get("next") || "/";

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const body = isReg ? f : { email: f.email, password: f.password };
      const { data } = await api.post(isReg ? "/auth/register" : "/auth/login", body);
      onAuth(data);
      toast.success(isReg ? "Welcome to ISoldIt!" : "Welcome back!");
      nav(next);
    } catch (e2) { setErr(formatErr(e2)); } finally { setBusy(false); }
  };

  return (
    <div className="mx-auto grid max-w-md px-4 py-16">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#d4f253]">{isReg ? "Free forever" : "Welcome back"}</p>
      <h1 className="mt-2 font-display text-3xl font-extrabold tracking-tight">{isReg ? "Create your account" : "Log in to ISoldIt"}</h1>
      <form onSubmit={submit} className="mt-8 space-y-4 rounded-3xl bg-[#173742] p-6 sm:p-8">
        {isReg && <input data-testid="auth-name-input" required className="field" placeholder="Your name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />}
        <input data-testid="auth-email-input" type="email" required className="field" placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
        <input data-testid="auth-password-input" type="password" required minLength={6} className="field" placeholder="Password (min 6 chars)" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} />
        {!isReg && <div className="text-right"><Link to="/forgot-password" data-testid="auth-forgot-link" className="text-xs font-semibold text-slate-400 hover:text-[#d4f253]">Forgot password?</Link></div>}
        {isReg && (
          <div className="flex gap-3">
            <StateSelect value={f.state} onChange={(v) => setF({ ...f, state: v })} testId="auth-state-select" light />
            <input data-testid="auth-suburb-input" className="field" placeholder="Suburb (optional)" value={f.suburb} onChange={(e) => setF({ ...f, suburb: e.target.value })} />
          </div>
        )}
        {err && <p data-testid="auth-error" className="rounded-lg bg-red-500/15 px-3 py-2 text-sm text-red-300">{err}</p>}
        {isReg && <p data-testid="auth-terms-note" className="text-center text-xs text-slate-400">By creating an account you agree to our <Link to="/terms" className="text-[#d4f253] hover:underline">Terms &amp; Conditions</Link>, <Link to="/disclaimer" className="text-[#d4f253] hover:underline">Disclaimer</Link> and <Link to="/privacy" className="text-[#d4f253] hover:underline">Privacy Policy</Link>.</p>}
        <button data-testid="auth-submit-btn" disabled={busy} className="btn-lime w-full py-3.5 text-sm">{busy ? "Please wait…" : isReg ? "Create account" : "Log in"}</button>
        <p className="text-center text-sm text-slate-400">
          {isReg ? "Already have an account? " : "New to ISoldIt? "}
          <Link data-testid="auth-switch-link" to={`${isReg ? "/login" : "/register"}?next=${encodeURIComponent(next)}`} className="font-semibold text-[#d4f253] hover:underline">{isReg ? "Log in" : "Sign up free"}</Link>
        </p>
      </form>
    </div>
  );
}
